/**
 * CSV / JSON export helpers — small, dependency-free serializers plus a
 * generic browser "save a Blob to disk" trigger.
 *
 * The XLSX side (export-xlsx.ts) leans on `XLSX.writeFile`, which owns its own
 * download. Lighter exports (a batches CSV, a single trace as CSV/JSON) don't
 * need the workbook machinery, so they live here and share `downloadBlob` —
 * the standard anchor-click pattern, the only client-side download path in the
 * app that isn't xlsx-specific.
 *
 * RFC 4180 quoting: a field is wrapped in double-quotes when it contains a
 * comma, double-quote, CR or LF; embedded quotes are doubled. A UTF-8 BOM is
 * prepended so Excel opens the file in the right encoding.
 *
 * Formula guard (#1089 review): a text cell a spreadsheet would run as a
 * formula (it starts with =, +, -, @, TAB or CR, and is no plain number) is
 * written with a leading apostrophe, so opening an export runs nothing. A
 * number, or text that is a plain number, is written as it is.
 *
 * Phase-1 swap point: these run entirely in the browser today. A server-side
 * `/v1/twin/...export` endpoint could later mint the same bytes and signed-URL
 * them back — callers would swap `downloadBlob` for following the URL.
 */

/** A CSV cell. `null`/`undefined` serialize to the empty string. */
export type CsvValue = string | number | boolean | null | undefined;

/** The first characters that make a spreadsheet run a cell as a formula. */
const FORMULA_START = /^[=+\-@\t\r]/;

/** Text that is a plain number ("-1.5", "+3", "2e-4"): a spreadsheet reads it as the number, never as a formula. */
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** Whether a text cell is guarded with a leading apostrophe: one a spreadsheet would run as a formula. */
export function csvFormulaGuarded(s: string): boolean {
  return FORMULA_START.test(s) && !PLAIN_NUMBER.test(s);
}

/** Quote a single field per RFC 4180 (only when it needs it), a text cell a spreadsheet would run guarded. */
function csvField(value: CsvValue): string {
  if (value === null || value === undefined) return "";
  let s = typeof value === "string" ? value : String(value);
  if (typeof value === "string" && csvFormulaGuarded(s)) s = `'${s}`;
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Serialize an array-of-arrays (header row + data rows) to a CSV string.
 * Rows are joined with CRLF, the line ending Excel and the spec expect.
 */
export function rowsToCsv(rows: CsvValue[][]): string {
  return rows.map((row) => row.map(csvField).join(",")).join("\r\n");
}

/**
 * Serialize an array of records to CSV given an explicit, ordered column set
 * (so the header order is deterministic and the caller controls labels). Each
 * column maps a header label to a value-extractor.
 */
export function objectsToCsv<T>(
  items: readonly T[],
  columns: ReadonlyArray<{ header: string; value: (item: T) => CsvValue }>,
): string {
  const header = columns.map((c) => c.header);
  const body = items.map((item) => columns.map((c) => c.value(item)));
  return rowsToCsv([header, ...body]);
}

/**
 * Trigger a browser download of `content` as a file. Wraps the content in a
 * Blob, mints an object URL, clicks a synthetic anchor, then revokes the URL.
 * No-op outside a DOM (SSR / tests without jsdom).
 */
export function downloadBlob(
  content: string,
  filename: string,
  mime: string,
): void {
  if (typeof document === "undefined") return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the click has committed the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Download a CSV string with a UTF-8 BOM (so Excel detects the encoding). */
export function downloadCsv(csv: string, filename: string): void {
  downloadBlob(`\uFEFF${csv}`, filename, "text/csv;charset=utf-8");
}

/** Download a value as pretty-printed JSON. */
export function downloadJson(value: unknown, filename: string): void {
  downloadBlob(
    JSON.stringify(value, null, 2),
    filename,
    "application/json;charset=utf-8",
  );
}

/**
 * Build a filesystem-safe, dated filename: `<base> · <YYYY-MM-DD>.<ext>`.
 * Mirrors the convention `export-xlsx.ts` uses for workbook names.
 */
export function makeExportFilename(base: string, ext: string): string {
  const safe = base
    .replace(/[^\w\s.-]/g, "")
    .replace(/\s+/g, " ")
    .trim() || "export";
  const date = new Date().toISOString().slice(0, 10);
  return `${safe} · ${date}.${ext}`;
}
